import {
  type Currency,
  type DropTableItem,
  formatAmount,
  getItemChance,
  getItemRarity,
  type StorefrontRarityKey,
  sortDropTableByValue,
} from "@loot-os/api-contract";

import { BoxImage } from "@loot-os/ui/components/box-image";
import { BoxRarityBadge } from "@loot-os/ui/components/box-rarity-badge";
import { LimitedItemBadge } from "@loot-os/ui/components/limited-item-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import {
  dropTableDisclosureNotes,
  isLimitedEntry,
  supplyMarkerText,
} from "@loot-os/ui/lib/marketplaceItemDisclosure";

/** How much of a counted item is left, or that none of it is. Nothing for an unlimited entry. */
function SupplyMarker({ item }: { item: DropTableItem }) {
  const text = supplyMarkerText(item);
  if (text === null) return null;
  const tone = item.isGemSubstitute ? "text-destructive" : "text-muted-foreground";
  return <span className={`${tone} text-xs`}>{text}</span>;
}

/**
 * Full odds disclosure for a box: every item, its value, rarity, and chance.
 *
 * Takes `items` + `priceCents` rather than a whole box so the dashboard can
 * render an unsaved draft. Two consequences of that, both deliberate:
 *
 *  - Chances are a ratio of the full roll range, not of these items' weight
 *    sum, so a half-edited table's column adds to less than 100%. That is the
 *    real per-open probability such a table would produce.
 *  - Rarity is derived from value ÷ price, so an unpriced draft would bucket
 *    every row as Common. Callers that can have no price must pass
 *    `showRarity={false}` and explain why, rather than show a column of
 *    confidently wrong chips.
 */
export function BoxDropTable({
  items,
  priceCents,
  currency,
  rarityColors,
  showRarity = true,
}: {
  items: readonly DropTableItem[];
  /** The box price, integer US cents; rarity is value ÷ price. */
  priceCents: number;
  /** The environment's display currency for the value column. */
  currency: Currency;
  rarityColors?: Partial<Record<StorefrontRarityKey, string>> | null;
  showRarity?: boolean;
}) {
  // publishVersion does not enforce itemId uniqueness, so a duplicate is
  // possible; suffix only the repeats, keeping the common case's key stable
  // across the re-sort that happens while staff edit values.
  const seen = new Map<string, number>();
  const rows = sortDropTableByValue(items).map((item) => {
    const nth = seen.get(item.itemId) ?? 0;
    seen.set(item.itemId, nth + 1);
    return { item, key: nth === 0 ? item.itemId : `${item.itemId}#${nth}` };
  });
  const notes = dropTableDisclosureNotes(items);
  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            {showRarity && <TableHead>Rarity</TableHead>}
            <TableHead className="text-right">Value</TableHead>
            <TableHead className="text-right">Chance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ item, key }) => (
            <TableRow key={key}>
              <TableCell>
                <span className="flex items-center gap-2">
                  <BoxImage src={item.imageUrl} className="size-8 rounded-md object-cover" />
                  <span className="flex flex-col items-start gap-0.5">
                    <span className="font-medium">{item.name}</span>
                    {isLimitedEntry(item) && <LimitedItemBadge item={item} currency={currency} />}
                    <SupplyMarker item={item} />
                  </span>
                </span>
              </TableCell>
              {showRarity && (
                <TableCell>
                  <BoxRarityBadge
                    rarity={getItemRarity(item.value, priceCents)}
                    colors={rarityColors}
                  />
                </TableCell>
              )}
              <TableCell className="text-right font-mono text-xs">
                {formatAmount(item.value, currency)}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {getItemChance(item.weight)}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {notes.map((note) => (
        <p key={note} className="rounded-md bg-muted px-3 py-2 text-muted-foreground text-xs">
          {note}
        </p>
      ))}
    </div>
  );
}
